/** A rate with its counts; `rate` is null below the report's minimum sample. */
export type InsightsRate = {
    successes: number;
    trials: number;
    rate: number | null;
    lowerBound: number | null;
};

/** Events per hour of wall exposure; `perHour` and `lowerBound` are null below the report's minimum exposure. */
export type InsightsHourlyRate = {
    count: number;
    exposureSeconds: number;
    perHour: number | null;
    lowerBound: number | null;
};

export type InsightsPhoto = {
    imageKey: string;
    title: string | null;
    thumbnailUrl: string | null;
    autoShown: number;
    autoInteracted: number;
    userOpened: number;
    uniqueOpeners: number;
    averageViewMs: number | null;
    zoomed: number;
    orderClicks: number;
    checkoutStarts: number;
    purchases: number;
    exposureSeconds: number;
    uniqueExposed: number;
    attention: InsightsRate;
    zoomRate: InsightsRate;
    tileOpenRate: InsightsHourlyRate;
    pullScore: number | null;
    pullRank: number;
};

/** One channel's row in the Sources table; `label` and `scans` are set only for `qr:` channels. */
export type InsightsSource = {
    channel: string;
    label: string | null;
    visitors: number;
    userOpened: number;
    orderClicks: number;
    checkoutStarts: number;
    purchases: number;
    scans: number | null;
};

export type InsightsReport = {
    range: string;
    channel: string | null;
    startUtc: string | null;
    /** The UTC midnight on or before `startUtc`, from which the per-day wall exposure counts; null for all time. */
    exposureStartUtc: string | null;
    /** The business time zone (IANA ID) whose midnight starts the today range. */
    timeZone: string;
    endUtc: string;
    generatedUtc: string;
    usedDailyTotals: boolean;
    dailyTotalsThrough: string | null;
    shopEnabled: boolean;
    minimumSample: number;
    minimumExposureSeconds: number;
    photos: InsightsPhoto[];
    rawRetentionDays: number;
    sources: InsightsSource[];
};

/**
 * One referring site behind the Other sites source, with the Sources table's columns. `host` is null for checkouts
 * and purchases that name no site (from before sites were recorded, or whose site could not be confirmed).
 */
export type InsightsReferrer = {
    host: string | null;
    visitors: number;
    userOpened: number;
    orderClicks: number;
    checkoutStarts: number;
    purchases: number;
    /** The site's referring pages, most visitors first; empty for the row that names no site. */
    paths: InsightsReferrerPath[];
};

/**
 * One referring page of a site: its path as stored (escaped, `/` for the site's address alone). Checkouts and
 * purchases are known only per site, so a page has none.
 */
export type InsightsReferrerPath = {
    path: string;
    visitors: number;
    userOpened: number;
    orderClicks: number;
};

/** GET /api/admin/insights/referrers: the Other sites breakdown, already in display order. */
export type InsightsReferrerReport = {
    range: string;
    startUtc: string | null;
    endUtc: string;
    generatedUtc: string;
    shopEnabled: boolean;
    rawRetentionDays: number;
    referrers: InsightsReferrer[];
};

/** The channel whose Sources row can open the breakdown by referring site. */
export const referrerBreakdownChannel = 'other';

/** True for the Sources row that offers the breakdown by referring site (Other sites). */
export function hasReferrerBreakdown(channel: string): boolean {
    return channel === referrerBreakdownChannel;
}

/**
 * A referring site's name as plain text: the host as reported (never turned into a link, since referrers are
 * untrusted), or the given label for the row of checkouts and purchases that name no site.
 */
export function formatReferrerHost(host: string | null, notRecordedLabel: string): string {
    return host == null || host.trim().length === 0 ? notRecordedLabel : host;
}

/**
 * True when a site's pages say more than the site's own row: at least one page other than the site's address
 * alone (`/`). A site reached only through its home page, and the row that names no site, offer no page list.
 */
export function hasReferrerPages(referrer: Pick<InsightsReferrer, 'host' | 'paths'>): boolean {
    return referrer.host != null && (referrer.paths ?? []).some(page => page.path !== '/' && page.path.length > 0);
}

/**
 * A referring page as plain text: its path exactly as stored (escaped, never decoded or linked, since it comes from
 * a visitor's browser), with the site's address alone shown as `/`.
 */
export function formatReferrerPath(path: string): string {
    return path.length === 0 ? '/' : path;
}

/** A choice in the channel selector; the empty value means every channel. */
export type InsightsChannelOption = {
    value: string;
    label: string;
};

const channelNames: Record<string, string> = {
    google: 'Google',
    bing: 'Bing',
    pinterest: 'Pinterest',
    instagram: 'Instagram',
    facebook: 'Facebook',
    other: 'Other sites',
    direct: 'Direct',
    unknown: 'Unknown (before tracking)'
};

/**
 * A channel in plain words, e.g. "pinterest" → "Pinterest", "other" → "Other sites", and "qr:k3x9p2" with its link's
 * label → "QR: Bristol café" (or "QR: k3x9p2" when the link is gone). Unrecognized values are shown as they are.
 */
export function formatChannelName(channel: string, label: string | null = null): string {
    if (channel.startsWith('qr:')) {
        const name = label != null && label.trim().length > 0 ? label.trim() : channel.slice(3);
        return `QR: ${name}`;
    }
    return channelNames[channel] ?? channel;
}

/**
 * The channel selector's choices: "All sources" first, then each channel in the Sources table's order. A selected
 * channel that the period has no row for (after changing the range, say) is kept as the last choice, so the
 * selector always shows the filter in force.
 */
export function buildChannelOptions(
    sources: readonly Pick<InsightsSource, 'channel' | 'label'>[],
    selected: string,
    allLabel: string
): InsightsChannelOption[] {
    const options: InsightsChannelOption[] = [{ value: '', label: allLabel }];
    const seen = new Set<string>();
    for (const source of sources) {
        if (seen.has(source.channel)) continue;
        seen.add(source.channel);
        options.push({ value: source.channel, label: formatChannelName(source.channel, source.label) });
    }
    if (selected.length > 0 && !seen.has(selected)) {
        options.push({ value: selected, label: formatChannelName(selected) });
    }
    return options;
}

/** Clicking a Sources row filters to its channel; clicking the row already filtered to goes back to every channel. */
export function nextChannelFilter(current: string, clicked: string): string {
    return current === clicked ? '' : clicked;
}

export type InsightsSortKey =
    | 'pull'
    | 'title'
    | 'autoShown'
    | 'attention'
    | 'exposure'
    | 'tileOpenRate'
    | 'userOpened'
    | 'averageView'
    | 'zoomRate'
    | 'orderClicks'
    | 'checkoutStarts'
    | 'purchases';

export type InsightsSortDirection = 'ascending' | 'descending';

/** A rate as a whole percent, e.g. 0.4235 → "42%". A non-zero rate under 1% shows as "<1%". */
export function formatPercent(rate: number): string {
    if (rate > 0 && rate < 0.005) return '<1%';
    return `${Math.round(rate * 100)}%`;
}

/** A rate's counts, e.g. "12 of 40". */
export function formatRateCounts(rate: Pick<InsightsRate, 'successes' | 'trials'>): string {
    return `${rate.successes.toLocaleString('en-US')} of ${rate.trials.toLocaleString('en-US')}`;
}

/** A view time, e.g. 4200 → "4.2 s", 42000 → "42 s", 125000 → "2 min 5 s"; null → "—". */
export function formatViewDuration(milliseconds: number | null): string {
    if (milliseconds == null || !Number.isFinite(milliseconds) || milliseconds < 0) return '—';
    const seconds = milliseconds / 1000;
    if (seconds < 9.95) return `${seconds.toFixed(1)} s`;
    const wholeSeconds = Math.round(seconds);
    if (wholeSeconds < 60) return `${wholeSeconds} s`;
    const minutes = Math.floor(wholeSeconds / 60);
    const remainder = wholeSeconds % 60;
    return remainder === 0 ? `${minutes} min` : `${minutes} min ${remainder} s`;
}

/** Wall exposure in minutes and seconds, e.g. 0 → "0 s", 45 → "45 s", 200 → "3 min 20 s", 7505 → "125 min 05 s". */
export function formatExposure(seconds: number): string {
    const wholeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;
    if (wholeSeconds < 60) return `${wholeSeconds} s`;
    const minutes = Math.floor(wholeSeconds / 60);
    const remainder = String(wholeSeconds % 60).padStart(2, '0');
    return `${minutes.toLocaleString('en-US')} min ${remainder} s`;
}

/** A rate per exposure hour, e.g. 0.25 → "0.25 per hour", 2.43 → "2.4 per hour", 12.6 → "13 per hour". */
export function formatPerHour(perHour: number): string {
    if (!Number.isFinite(perHour) || perHour <= 0) return '0 per hour';
    if (perHour < 0.01) return '<0.01 per hour';
    const text = perHour < 0.995
        ? perHour.toFixed(2)
        : perHour < 9.95 ? perHour.toFixed(1) : Math.round(perHour).toLocaleString('en-US');
    return `${text} per hour`;
}

/** A tile open rate's counts, e.g. "12 opens in 300 min 00 s". */
export function formatHourlyRateCounts(rate: Pick<InsightsHourlyRate, 'count' | 'exposureSeconds'>): string {
    return `${rate.count.toLocaleString('en-US')} open${rate.count === 1 ? '' : 's'} in ${formatExposure(rate.exposureSeconds)}`;
}

/** The range key whose window starts at midnight in the business time zone rather than N days ago. */
export const todayRange = 'today';

/**
 * A time zone's everyday name, e.g. "Eastern Time" for America/New_York, from the browser's own names. Falls back
 * to the zone's ID when the browser does not know the zone or has no generic name for it.
 */
export function formatTimeZoneName(timeZone: string, at: Date = new Date()): string {
    try {
        const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
            .formatToParts(at)
            .find(part => part.type === 'timeZoneName')?.value;
        return name != null && name.length > 0 ? name : timeZone;
    } catch {
        return timeZone;
    }
}

/**
 * When the today range's wall exposure starts, as a clock time in the business time zone (e.g. "8:00 PM"), or null
 * when it starts with the range. Exposure is stored per UTC day, so it counts from the UTC midnight on or before
 * the local midnight: the previous evening for a zone behind UTC. An unknown zone shows the time in UTC.
 */
export function formatTodayExposureStart(
    current: Pick<InsightsReport, 'startUtc' | 'exposureStartUtc' | 'timeZone'>
): string | null {
    if (current.startUtc == null || current.exposureStartUtc == null) return null;
    const exposureStart = new Date(current.exposureStartUtc);
    if (!(exposureStart.getTime() < new Date(current.startUtc).getTime())) return null;
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
    try {
        return exposureStart.toLocaleTimeString('en-US', { ...options, timeZone: current.timeZone });
    } catch {
        return `${exposureStart.toLocaleTimeString('en-US', { ...options, timeZone: 'UTC' })} UTC`;
    }
}

/** The first click on a header sorts its best values first: rank 1 and A–Z ascending, counts and rates descending. */
export function defaultSortDirection(key: InsightsSortKey): InsightsSortDirection {
    return key === 'pull' || key === 'title' ? 'ascending' : 'descending';
}

/**
 * Sorts report rows for the table. Missing values (hidden rates, no view time, removed titles) always sort last,
 * whatever the direction; ties fall back to the "Strongest pull" rank so the order is stable.
 */
export function sortInsightsPhotos(
    photos: readonly InsightsPhoto[],
    key: InsightsSortKey,
    direction: InsightsSortDirection
): InsightsPhoto[] {
    const sign = direction === 'ascending' ? 1 : -1;
    return [...photos].sort((left, right) => {
        const leftValue = sortValue(left, key);
        const rightValue = sortValue(right, key);
        if (leftValue == null || rightValue == null) {
            if (leftValue != null) return -1;
            if (rightValue != null) return 1;
        } else {
            const difference = typeof leftValue === 'string' && typeof rightValue === 'string'
                ? leftValue.localeCompare(rightValue, 'en', { sensitivity: 'base', numeric: true })
                : Number(leftValue) - Number(rightValue);
            if (difference !== 0) return difference * sign;
            const evidence = secondaryValue(right, key) - secondaryValue(left, key);
            if (evidence !== 0) return evidence;
        }
        return left.pullRank - right.pullRank;
    });
}

/** The dashboard teaser's photo: the best-ranked photo with enough data and a current title, or null. */
export function pickTeaserPhoto(photos: readonly InsightsPhoto[]): InsightsPhoto | null {
    return [...photos]
        .filter(photo => photo.pullScore != null && photo.title != null && photo.title.length > 0)
        .sort((left, right) => left.pullRank - right.pullRank)[0] ?? null;
}

function sortValue(photo: InsightsPhoto, key: InsightsSortKey): number | string | null {
    switch (key) {
        case 'pull': return photo.pullRank;
        case 'title': return photo.title;
        case 'autoShown': return photo.autoShown;
        case 'attention': return photo.attention.rate;
        case 'exposure': return photo.exposureSeconds;
        case 'tileOpenRate': return photo.tileOpenRate.perHour;
        case 'userOpened': return photo.userOpened;
        case 'averageView': return photo.averageViewMs;
        case 'zoomRate': return photo.zoomRate.rate;
        case 'orderClicks': return photo.orderClicks;
        case 'checkoutStarts': return photo.checkoutStarts;
        case 'purchases': return photo.purchases;
    }
}

/** Between equal rates, the larger sample first; between equal opens or exposure, more distinct visitors first. */
function secondaryValue(photo: InsightsPhoto, key: InsightsSortKey): number {
    switch (key) {
        case 'attention': return photo.attention.trials;
        case 'zoomRate': return photo.zoomRate.trials;
        case 'exposure': return photo.uniqueExposed;
        case 'tileOpenRate': return photo.tileOpenRate.exposureSeconds;
        case 'userOpened': return photo.uniqueOpeners;
        default: return 0;
    }
}
